'use client'

import { useCart } from '@shopify/hydrogen-react'
import { useEffect, useState } from 'react'

import { DEFAULT_CURRENCY, AnalyticsEvent, CartActions } from '@/lib/analytics/Analytics.constants'

import { getAddedAndRemovedItems, getCartValue, getGtmItems, gtm } from './Analytics.utils'

import type { GoogleCartPayload } from './Analytics.types'
import type { CartActionsType } from '@/lib/analytics/Analytics.constants'
import type { CartWithActions } from '@shopify/hydrogen-react'
import type { CartLine as CartLineType } from '@shopify/hydrogen-react/storefront-api-types'

/**
 * Since hydrogen-react does not provide the cart object being returned in cart events,
 * we have to manually track the carts and send the events when the cart is updated.
 *
 * This component tracks the add to cart and remove from cart events.
 */
export function AnalyticsCart({ action, clear }: { action: CartActionsType | null; clear: () => void }) {
  const currentCart = useCart()
  const [previousCart, setPreviousCart] = useState<CartWithActions | null>(currentCart)
  const status = currentCart?.status

  useEffect(() => {
    if (!action || status !== 'idle' || !currentCart) return

    const previousCartLines = previousCart?.lines ? (previousCart?.lines as CartLineType[]) : []
    const currentCartLines = currentCart.lines ? (currentCart.lines as CartLineType[]) : []

    const currency = currentCart.cost?.totalAmount?.currencyCode || DEFAULT_CURRENCY

    const { itemsAdded, itemsRemoved } = getAddedAndRemovedItems(currentCartLines, previousCartLines)

    if (action === CartActions.ADD) {
      if (itemsAdded.length === 0) return console.warn('Error in add to cart event, no items found') // eslint-disable-line no-console

      const value = getCartValue(itemsAdded)
      const items = getGtmItems(itemsAdded)

      const payload: GoogleCartPayload = {
        currency,
        value,
        items,
      }

      gtm.push(AnalyticsEvent.PRODUCT_ADD_TO_CART, payload)
    }

    if (action === CartActions.REMOVE) {
      if (itemsRemoved.length === 0) return console.warn('Error in remove from cart event, no items found') // eslint-disable-line no-console

      const value = getCartValue(itemsRemoved)
      const items = getGtmItems(itemsRemoved)

      const payload: GoogleCartPayload = {
        currency,
        value,
        items,
      }

      gtm.push(AnalyticsEvent.PRODUCT_REMOVED_FROM_CART, payload)
    }

    if (action === CartActions.UPDATE) {
      if (itemsAdded.length === 0 && itemsRemoved.length === 0) return console.warn('Error in update cart event, no items found in added or removed items') // eslint-disable-line no-console

      if (itemsAdded.length > 0) {
        const value = getCartValue(itemsAdded)
        const items = getGtmItems(itemsAdded)

        const payload: GoogleCartPayload = {
          currency,
          value,
          items,
        }

        gtm.push(AnalyticsEvent.PRODUCT_ADD_TO_CART, payload)
      }

      if (itemsRemoved.length > 0) {
        const value = getCartValue(itemsRemoved)
        const items = getGtmItems(itemsRemoved)

        const payload: GoogleCartPayload = {
          currency,
          value,
          items,
        }

        gtm.push(AnalyticsEvent.PRODUCT_REMOVED_FROM_CART, payload)
      }
    }

    // Update the previous cart then clear the actions
    setPreviousCart(currentCart)
    clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, status, currentCart?.id])

  return null
}
